import {
    DownloadOutlined,
    ExportOutlined,
    FileOutlined,
} from '@ant-design/icons';
import { Button, Space, Spin, message as antMessage } from 'antd';
import { downloadAIFile } from '../actions';
import clsx from 'clsx';
import AiSteps from './ai-steps';

import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Message } from '../vitrual-assistant.type';
import { KgdAvatar } from './avatar/kgd-avatar';
import { UserAvatar } from './avatar/user-avatar';
import { localize } from 'mixins/helpers';

interface ChatMessageProps {
    message: Message;
    action?: () => void;
    showAvatar: boolean;
    translations?: any;
}

const ChatMessageAi = ({
    message: {
        from,
        text,
        fileUrl,
        loading,
        attachments,
        steps,
        startedAt,
        finishedAt,
        status,
    },
    action,
    showAvatar,
    translations,
}: ChatMessageProps) => {
    const showAssistantAvatar = showAvatar && from === 'assistant';
    const showUserAvatar = showAvatar && from === 'user';
    const isAssistantMessage = from === 'assistant';
    const [downloadingFileId, setDownloadingFileId] =
        React.useState<string | null>(null);

    const handleDownload = async (
        fileId: string,
        fileName: string
    ) => {
        if (downloadingFileId) return;

        setDownloadingFileId(fileId);

        try {
            await downloadAIFile(fileId, fileName);
        } catch (error) {
            console.error('AI file download failed:', error);
            antMessage.error(
                'Не удалось скачать файл. Попробуйте повторить запрос'
            );
        } finally {
            setDownloadingFileId(null);
        }
    };

    return (
        <li
            className={clsx('message__wrapper', `message__wrapper__${from}`, {
                show_avatar: showAvatar,
            })}
        >
            {showAssistantAvatar && <KgdAvatar />}

            <div
                className={clsx('message', `message__${from}`, {
                    message_clickable: Boolean(action),
                })}
                {...(action ? { onClick: action } : {})}
            >
                <Space align="start">
                    {action && <ExportOutlined />}
                    {isAssistantMessage && status && startedAt ? (
                        <div className="message__markdown">
                            <AiSteps
                                steps={steps || []}
                                status={status}
                                startedAt={startedAt}
                                finishedAt={finishedAt}
                                hasText={Boolean(text)}
                            />
                            <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                    a: ({ children, href }) => {
                                        if (!href || href === '#') {
                                            return <span>{children}</span>;
                                        }

                                        return (
                                            <a
                                                href={href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="message__download_link"
                                            >
                                                {children}
                                            </a>
                                        );
                                    },
                                    table: ({ children }) => (
                                        <div className="message__table_wrapper">
                                            <table>{children}</table>
                                        </div>
                                    ),
                                }}
                            >
                                {text}
                            </ReactMarkdown>

                            {attachments?.map(attachment => (
                                <div
                                    key={attachment.fileId}
                                    className="message__ai_file"
                                >
                                    <div className="message__ai_file_name">
                                        <FileOutlined />
                                        <span>{attachment.name}</span>
                                    </div>

                                    <Button
                                        icon={<DownloadOutlined />}
                                        loading={downloadingFileId === attachment.fileId}
                                        disabled={
                                            Boolean(downloadingFileId) &&
                                            downloadingFileId !== attachment.fileId
                                        }
                                        onClick={event => {
                                            event.stopPropagation();
                                            void handleDownload(
                                                attachment.fileId,
                                                attachment.name
                                            );
                                        }}
                                    >
                                        {localize('button.download-file', translations)}
                                    </Button>
                                </div>
                            ))}

                            {status === 'stopped' && (
                                <div className="message__ai_status">
                                    Генерация остановлена
                                </div>
                            )}

                            {status === 'error' && (
                                <div className="message__ai_status">
                                    Не удалось завершить ответ
                                </div>
                            )}

                            {fileUrl && !attachments?.length && (
                                <a
                                    href={fileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="message__download_link"
                                >
                                    {localize('button.download-file', translations)}
                                </a>
                            )}
                        </div>

                    ) : loading ? (
                        <>
                            <Spin size="small" />
                            <span className="message__text">{text}</span>
                        </>
                    ) : isAssistantMessage ? (
                        <div className="message__markdown">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {text}
                            </ReactMarkdown>
                        </div>
                    ) : (
                        <span className="message__text">{text}</span>
                    )}
                </Space>
            </div>

            {showUserAvatar && <UserAvatar />}
        </li>
    );
};

export default ChatMessageAi;
